// Precedence rules between the three brand-threat signals:
//   1. Google Web Risk listing (hard, third-party, homonym-safe) — always wins.
//   2. GPT-5.2 adjudicator verdict (advisory) — resolves homonym ambiguity both
//      ways, but can never mask a Web Risk hit.
//   3. Deterministic scoring factors — the fallback when neither is present.
// The backend mirrors these rules for TakedownEligible and the takedown gate
// (BrandProtectionController) — keep both in sync.

// Tier 2 precision split, HOMONYM-SAFE. "Confirmed" = only signals a legitimate
// same-name entity (National Bank of Poland vs Pakistan, NBP Nutrition, …) cannot
// trip: an exact logo/favicon clone of OUR brand, a third-party confirmed-phishing
// listing, a lookalike domain directly serving an APK, or a cert mismatch on OUR
// OWN package (repackaged). DELIBERATELY excluded: lexical label matches
// (nbp.pl), content/title mentions, and a different-package cert mismatch
// (Poland's app has its own cert) — a real homonym fires all of those, so they go
// to "Possible" for AI/analyst review, not an automatic "Confirmed".
export const CONFIRMED_FACTOR_KEYS = new Set(["favicon-clone", "feed-confirmed", "serves-apk", "web-risk"]);

// The advisory AI verdict cleared this threat as a legitimate same-name entity —
// EXCEPT when Google Web Risk lists the same URL: that hard signal is never
// masked by the vision call, which can misjudge a compromised look-alike.
export function isAiCleared(t, aiVerdict) {
  return aiVerdict?.verdict === "LegitimateDifferentEntity" && !t?.webRiskThreatType;
}

// The GPT-5.2 adjudicator's verdict overrides deterministic tiering in BOTH
// directions — it is the only thing that can promote a real homonym fake (a
// title-only replit.app phish) to Confirmed, or drop a legitimate same-name
// entity (National Bank of Poland) out of it. Uncertain / no verdict falls
// through to the deterministic homonym-safe signals below.
export function threatTier(t, aiVerdict) {
  if (aiVerdict?.verdict === "Impersonation") return "Confirmed";
  // AI-cleared same-name entities get their own bucket — they no longer belong in
  // "Possible" (which means "not yet resolved"); the AI already resolved them.
  if (isAiCleared(t, aiVerdict)) return "LikelyLegitimate";
  // Web Risk escalations are sticky: the column can outlive the "web-risk"
  // scoring factor across rescans, so check it directly too.
  if (t?.webRiskThreatType) return "Confirmed";
  const keys = (t?.scoringFactors || []).map(f => f.key);
  if (keys.some(k => CONFIRMED_FACTOR_KEYS.has(k))) return "Confirmed";
  // Cert mismatch is dispositive only when it's our package resigned (Repackaged);
  // a different package signed with a different key is just a same-name app.
  if (t?.authenticity === "CryptographicFail" && t?.incidentType === "Repackaged") return "Confirmed";
  return "Possible";
}

// AI-cleared same-name entities are downgraded to Low severity — an AI-confirmed
// legitimate business is not a Critical/High brand threat, whatever the
// deterministic score originally said.
export function effectiveRiskLevel(t, aiVerdict) {
  if (isAiCleared(t, aiVerdict) && (t.riskLevel === "Critical" || t.riskLevel === "High")) {
    return "Low";
  }
  return t.riskLevel;
}

// Takedown is off the table for an AI-cleared entity (filing an abuse report on
// a legitimate third party is exactly what the gate exists to prevent), even if
// the server-computed takedownEligible flag predates the verdict.
export function isTakedownAllowed(t, aiVerdict) {
  return !!t?.takedownEligible && !isAiCleared(t, aiVerdict);
}
